import type { InitOptions } from 'i18next';
import commonEn from '../locales/en/common.json';
import dashboardEn from '../locales/en/dashboard.json';
import emailEn from '../locales/en/email.json';
import mobileEn from '../locales/en/mobile.json';

/** UI languages. RU and UK are added in Task 13 with the same key set as English. */
export const SUPPORTED_LANGUAGES = ['en'] as const;
export type Language = (typeof SUPPORTED_LANGUAGES)[number];
export const DEFAULT_LANGUAGE: Language = 'en';

export const NAMESPACES = ['common', 'mobile', 'dashboard', 'email'] as const;
export type Namespace = (typeof NAMESPACES)[number];
export const DEFAULT_NAMESPACE = 'common' satisfies Namespace;

export const resources = {
  en: { common: commonEn, mobile: mobileEn, dashboard: dashboardEn, email: emailEn },
} as const satisfies Record<Language, Record<Namespace, unknown>>;

function isSupported(language: string): language is Language {
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(language);
}

/**
 * First supported language from the phone's preferred list (e.g. `["uk-UA", "en-GB"]`),
 * else English (Q17: phone language by default, changeable in Settings later).
 */
export function pickLanguage(preferred: readonly (string | null | undefined)[]): Language {
  for (const tag of preferred) {
    const base = tag?.toLowerCase().split(/[-_]/)[0];
    if (base !== undefined && isSupported(base)) {
      return base;
    }
  }
  return DEFAULT_LANGUAGE;
}

/** Shared i18next options for the app, the dashboard and the PDF. Namespace `email` is used by
 * the Edge Functions through a checked copy (`supabase/functions/_shared/texts.json`). */
export function i18nOptions(language: Language): InitOptions {
  return {
    resources,
    lng: language,
    fallbackLng: DEFAULT_LANGUAGE,
    ns: [...NAMESPACES],
    defaultNS: DEFAULT_NAMESPACE,
    interpolation: { escapeValue: false },
    returnNull: false,
  };
}

declare module 'i18next' {
  interface CustomTypeOptions {
    defaultNS: typeof DEFAULT_NAMESPACE;
    resources: (typeof resources)['en'];
  }
}
